import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import styled from "styled-components";
import { borders, colors, fonts } from "../../common/mixins";
import { DsTextField, Title } from "../../../common";
import { Box, InputAdornment } from "@mui/material";
import { formatPrice, parsePrice } from "../../../utils/Utils";
import { DonationCampaign, Ticket } from "../../../types/reservation";
import { useFormContext, useWatch } from "react-hook-form";
import type { ReservationFormValues } from "../../../pageComponents/reservationPage/reservation-page.schema";
import {
  getDonationAmountBounds,
  computeDonationDefaultAmount,
} from "../../../pageComponents/reservationPage/utils/donationUtils";
import { useReservationResources } from "../../../pageComponents/reservationPage/hooks/useReservationResources";
import { BREAKPOINT_RESERVATION_PAGE } from "../../../pageComponents/reservationPage/constants/breakpoints";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  margin-top: 16px;
  border-radius: 8px;
  gap: 20px;
  ${borders.lightGrayBorder};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ImgContainer = styled.div`
  width: 68px;
  height: 68px;
  border-radius: 8px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
  }
`;

const TextTitle = styled.h2`
  ${fonts.smallTitle()}
  margin: 0;
`;

const Text = styled.div`
  ${fonts.normalText()};
  margin: 0;
  p {
    margin: 0;
  }
`;

const FieldRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const CurrencyText = styled.span`
  ${fonts.normalText()};
`;

const StepButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 49px;
  height: 48px;
  border-radius: 8px;
  ${borders.formActiveBorder()}
  background: #fff;
  cursor: pointer;

  @media (hover: hover) {
    &:not(:disabled):hover {
      background: #f3f6f9;
    }
  }

  &:not(:disabled):active {
    background: #f3f6f9;
    color: #0d0c1c;
  }

  font-size: 20px;
  color: #0d0c1c;

  &:focus-visible {
    outline: none;
    border-color: ${colors.primary};
  }

  &:disabled {
    color: #d8dbe0;
    border-color: #d8dbe0;
    cursor: default;
  }

  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    width: 41px;
    height: 41px;
  }
`;

interface DonationsProps {
  donationCampaign: DonationCampaign | null;
  tickets: Ticket[];
}

export function Donations({ donationCampaign, tickets }: DonationsProps) {
  const r = useReservationResources();

  const {
    control,
    formState: { errors, isSubmitted },
    clearErrors,
    setValue,
  } = useFormContext<ReservationFormValues>();
  const donationAmount = useWatch({ control, name: "donationAmount" });

  const [editingValue, setEditingValue] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);

  const stepAmount = donationCampaign?.stepAmount ?? 1;

  const { min: minAmount, max: maxAmount } = useMemo(
    () =>
      donationCampaign
        ? getDonationAmountBounds(donationCampaign, tickets)
        : { min: 0, max: Infinity },
    [donationCampaign, tickets],
  );

  const ticketsTotal = useMemo(
    () => tickets.reduce((sum, t) => sum + (t.price ?? 0), 0),
    [tickets],
  );

  const defaultAmount = useMemo(
    () =>
      donationCampaign
        ? computeDonationDefaultAmount(donationCampaign, tickets, ticketsTotal)
        : 0,
    [donationCampaign, tickets, ticketsTotal],
  );

  const donationError = errors.donationAmount?.message;

  // Single place that commits a value to the form and decides whether to
  // validate immediately. Kept as one function so the "when do we
  // validate" rule can't drift out of sync between call sites again (it's
  // been patched twice before when it lived inline in each handler).
  const commitDonationAmount = (amount: number, validateNow: boolean) => {
    setValue("donationAmount", amount, { shouldValidate: validateNow });
  };

  // Push the campaign-computed default into the form whenever it changes
  // (e.g. campaign loads, or product/ticket changes recompute it). Deliberately
  // NOT keyed on isSubmitted — that only flips false->true once, on the first
  // submit attempt, and including it here would re-run this effect at that
  // moment and stomp whatever the user had typed with the campaign default.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    commitDonationAmount(defaultAmount ?? 0, isSubmitted);
  }, [defaultAmount, donationCampaign, setValue]);

  if (!donationCampaign) return null;

  const disabled = minAmount > 0 && minAmount === maxAmount;
  const committedAmount = donationAmount ?? 0;
  const currency = donationCampaign.currencySymbol ?? "";

  // Display: editing text while typing, otherwise formatted committed value (no currency — shown as adornment)
  const formatValue = (amount: number) => formatPrice(amount, "");

  const fieldValue =
    editingValue !== null
      ? editingValue
      : committedAmount > 0
        ? formatValue(committedAmount)
        : "";

  const changeAmount = (delta: number) => {
    const base =
      editingValue !== null ? parsePrice(editingValue) : committedAmount;
    const newAmount = Math.min(maxAmount, Math.max(minAmount, base + delta));
    setEditingValue(null);
    commitDonationAmount(newAmount, !!donationError);
  };

  const handleIncrement = () => changeAmount(stepAmount);
  const handleDecrement = () => changeAmount(-stepAmount);

  // While typing: update local editing text; when there's a visible error also
  // commit to the form so it re-validates immediately.
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === "") {
      setEditingValue("");
      clearErrors("donationAmount");
      return;
    }

    if (/^\d*([.,]\d{0,2})?$/.test(value)) {
      setEditingValue(value);
      if (donationError) {
        const num = parsePrice(value);
        if (num > 0) commitDonationAmount(num, true);
      }
    }
  };

  // On blur: commit edited value to the form. Required errors (empty/zero)
  // are deferred to the next submit, like every other field — otherwise
  // clearing the field re-shows the error right away. Range errors show
  // on blur immediately.
  const handleBlur = () => {
    if (editingValue === null) return;
    const committed = editingValue.trim() === "" ? 0 : parsePrice(editingValue);
    setEditingValue(null);
    commitDonationAmount(committed, committed > 0);
  };

  return (
    <Box id="additional-items-section">
      <Title>{r.AdditionalItems}</Title>
      <Wrapper>
        <Container>
          {donationCampaign.donationImageUrl && !imgError && (
            <ImgContainer>
              <img
                src={`${window.location.origin}/${donationCampaign.donationImageUrl}`}
                alt="Donation"
                width="100%"
                height="100%"
                onError={() => setImgError(true)}
              />
            </ImgContainer>
          )}

          <div>
            <TextTitle>{donationCampaign.title}</TextTitle>
            <Text
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(donationCampaign.description ?? ""),
              }}
            />
          </div>
        </Container>
        <FieldRow>
          <DsTextField
            id="donation-field"
            label={donationCampaign.donationAmountLabel ?? r.DonationAmount}
            value={fieldValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            type="text"
            error={!!donationError}
            helperText={donationError}
            disabled={disabled}
            showCheckOnDisabled={false}
            InputProps={{
              sx: {
                "& .MuiInputBase-inputAdornedStart": { paddingLeft: "0" },
              },
              startAdornment:
                currency && fieldValue ? (
                  <InputAdornment position="start">
                    <CurrencyText>{currency}</CurrencyText>
                  </InputAdornment>
                ) : undefined,
            }}
            inputProps={{
              inputMode: "decimal",
              readOnly: donationCampaign.disableAmountTyping,
            }}
            fullWidth
          />

          <>
            <StepButton
              id="decrement-step-btn"
              type="button"
              onClick={handleDecrement}
              disabled={disabled || committedAmount <= minAmount}
            >
              −
            </StepButton>
            <StepButton
              id="increment-step-btn"
              type="button"
              onClick={handleIncrement}
              disabled={disabled || committedAmount >= maxAmount}
            >
              +
            </StepButton>
          </>
        </FieldRow>
      </Wrapper>
    </Box>
  );
}
